import React, { useState } from "react";
import { Box, Typography, Button, Grid, TextField } from "@mui/material";
import { useNavigate } from 'react-router-dom';
import { submitApplication } from '../../services/applicationApi';
import SuccessPopupModal from "../common/success_popup_modal";
import FailedPopupModal from "../common/failed_popup_modal";

// Helper function to parse HTML and convert to React components with proper styling
const parseDescription = (htmlString) => {
    if (!htmlString) return null;

    const elements = [];
    let index = 0;

    // Create a temporary div to parse the HTML and maintain order
    const tempDiv = document.createElement('div');
    tempDiv.innerHTML = htmlString;

    // Process each child node in order
    Array.from(tempDiv.children).forEach((child) => {
        console.log('Processing element:', child.tagName, child.textContent?.trim());

        // Check if it's a Typography element (case insensitive)
        if (child.tagName.toLowerCase() === 'typography') {
            // Extract the text content (the heading text)
            const headingText = child.textContent.trim();
            console.log('Found Typography heading:', headingText);

            // Apply the correct Typography styling
            elements.push(
                <Typography key={index++} sx={{
                    fontSize: { xs: '20px', md: '32px' },
                    mb: 1,
                    mt: 2,
                    fontFamily: 'Wix Madefor Display, sans-serif',
                    fontWeight: 700,
                    color: '#fff'
                }}>
                    {headingText}
                </Typography>
            );
        } else if (child.tagName.toLowerCase() === 'box') {
            // Extract Box props and list items
            const sxProps = {
                pl: 3,
                mb: 2,
                fontSize: { xs: '14px', md: '24px' },
                fontFamily: 'Inter, sans-serif',
                fontWeight: 500,
                lineHeight: '160%'
            };

            const listItems = Array.from(child.querySelectorAll('li')).map((li, liIndex) => (
                <li key={liIndex}>{li.textContent}</li>
            ));

            console.log('Found Box with', listItems.length, 'list items');

            elements.push(
                <Box key={index++} component="ul" sx={sxProps}>
                    {listItems}
                </Box>
            );
        } else {
            console.log('Unknown element:', child.tagName);
        }
    });

    console.log('Total elements created:', elements.length);
    return elements;
};

const ApplicationComponent = ({ position }) => {
    const navigate = useNavigate();
    // const formRef = React.useRef();

    // Function to scroll to the form
    // const scrollToForm = () => {
    //     if (formRef.current) {
    //         formRef.current.scrollIntoView({ 
    //             behavior: 'smooth', 
    //             block: 'start' 
    //         });
    //     }
    // };

    // If no position is provided, show a message or return null
    if (!position) {
        return (
            <Box sx={{
                minHeight: '50vh',
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                justifyContent: 'center',
                color: '#fff',
                p: 4,
                textAlign: 'center',
                maxWidth: '1280px',
                mx: 'auto',
                width: '100%'
            }}>
                <Typography sx={{
                    fontSize: { xs: '18px', md: '24px' },
                    fontFamily: 'Inter, sans-serif',
                    mb: 3,
                    color: '#939398'
                }}>
                    No position selected or position not found.
                </Typography>
                <Typography sx={{
                    fontSize: { xs: '14px', md: '16px' },
                    fontFamily: 'Inter, sans-serif',
                    mb: 4,
                    color: '#939398'
                }}>
                    The position you're looking for may have been removed or is no longer available.
                </Typography>
                <Button
                    onClick={() => navigate('/careers')}
                    variant="contained"
                    sx={{
                        backgroundColor: '#8ABEFC',
                        color: '#000',
                        fontWeight: 600,
                        fontSize: { xs: '14px', md: '16px' },
                        px: 3,
                        py: 1.5,
                        borderRadius: '8px',
                        textTransform: 'none',
                        '&:hover': {
                            backgroundColor: '#7aaeec'
                        }
                    }}
                >
                    Browse Available Positions
                </Button>
            </Box>
        );
    }

    return (
        <Box sx={{
                        background:'#000000ff',
             maxWidth: '1440px',
            mx: 'auto',
            width: '100%'
        }}>
            <Box
                flexDirection={{ xs: "column", md: "row" }}
                sx={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: { xs: "center", md: "space-between" },
                    background: "#ffffffff",
                    borderRadius: "10px",
                    px: 7.5, // 60px
                    py: 5,   // 40px
                    my: 2.5, // 20px
                    maxHeight: { xs: "180px", md: "200px" },
                    mx: { xs: 2, md: 6 },
                }}
            >
                <Box sx={{ textAlign: { xs: 'center', md: 'left' } }}>
                    <Typography
                        sx={{
                            color: "#000000ff",
                            fontSize: { xs: "24px", md: "40px" },
                            fontWeight: 700,
                            mb: { xs: 1, md: 1 }, // 20px
                            fontFamily: 'Wix Madefor Display, sans-serif',
                        }}
                    >
                        {position.title}
                    </Typography>
                    <Typography sx={{ color: "#000000ff", fontSize: { xs: "14px", md: "20px" }, fontFamily: 'Inter, sans-serif' }}>
                        <Box component="span" fontWeight={500}>Work Type :</Box>
                        <Box component="span" ml={1}>{position.type}</Box>
                    </Typography>
                </Box>
                <Button
                   // onClick={scrollToForm}
                    onClick={() => position.applyLink ? window.open(position.applyLink, '_blank') : alert('Application link not set.')}
                    variant="contained"
                    sx={{
                        background: "#000000ff",
                        color: "#ffffffff",
                        fontWeight: 700,
                        fontSize: { xs: "14px", md: "20px" },
                        borderRadius: { xs: "8px", md: "10px" },
                        px: { xs: "16px", md: "20px" },
                        py: { xs: "8px", md: "10px" },
                        boxShadow: "none",
                        textTransform: "none",
                        mt: { xs: 2, md: 0 },
                    }}
                >
                    Apply Now
                </Button>
            </Box>
            <JobDescription position={position} />
            {/* <ApplicationForm ref={formRef} position={position} /> */}
        </Box>
    );
};

const JobDescription = ({ position }) => (
    <Box
        sx={{
            background:'#000000ff',
            color: '#fff',
            p: { xs: 2, md: 5 },
            mt: 3,
            mx: { xs: 2, md: 6 },
            fontFamily: 'Inter, sans-serif',
        }}
    >
        {/* Static company description - unchanged */}
        <Typography sx={{ fontSize: { xs: '14px', md: '24px' }, mb: 2, fontFamily: 'Inter, sans-serif', fontWeight: 500, color: '#ffffffff', lineHeight: '160%' }}>
            At Rubaai, we're building the future of digital payments in Sri Lanka. From quick bill payments to secure money transfers and QR code transactions, we're here to make your financial life easier, safer, and smarter. We believe that everyone should have access to simple, cashless solutions whether you're shopping online, paying your utility bills, or supporting your local charities.
        </Typography>

        {/* Dynamic position description with proper styling */}
        {position.description ? parseDescription(position.description) : (
            <Typography sx={{
                fontSize: { xs: '14px', md: '24px' },
                fontFamily: 'Inter, sans-serif',
                fontWeight: 500,
                color: '#ffffffff',
                lineHeight: '160%',
                fontStyle: 'italic'
            }}>
                Position description not available.
            </Typography>
        )}
    </Box>
);

const ApplicationForm = React.forwardRef(({ position }, ref) => {
    const [socialLinks, setSocialLinks] = useState(['']);
    const [validationErrors, setValidationErrors] = useState([false]);
    const [selectedFile, setSelectedFile] = useState(null);
    const [formData, setFormData] = useState({
        firstName: '',
        lastName: '',
        email: '',
        contactNumber: ''
    });
    const [errors, setErrors] = useState({});
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [submitError, setSubmitError] = useState('');
    const fileInputRef = React.useRef();

    // Modal state management
    const [successModalOpen, setSuccessModalOpen] = useState(false);
    const [failedModalOpen, setFailedModalOpen] = useState(false);
    const [modalMessage, setModalMessage] = useState('');

    // Validation functions
    const validateEmail = (email) => {
        const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
        return emailRegex.test(email);
    };

    const validatePhone = (phone) => {
        const phoneRegex = /^[+]?[1-9][\d]{0,15}$/;
        return phoneRegex.test(phone.replace(/[\s\-()]/g, ''));
    };

    const validateField = (name, value) => {
        switch (name) {
            case 'firstName':
            case 'lastName':
                return value.trim().length >= 2 ? '' : `${name === 'firstName' ? 'First name' : 'Last name'} must be at least 2 characters long`;
            case 'email':
                if (!value.trim()) return 'Email is required';
                return validateEmail(value) ? '' : 'Please enter a valid email address';
            case 'contactNumber':
                if (!value.trim()) return 'Contact number is required';
                return validatePhone(value) ? '' : 'Please enter a valid phone number';
            case 'resume':
                return value ? '' : 'Please select a resume file';
            default:
                return '';
        }
    };

    const handleAddSocialLink = () => {
        // Check if the last field is not empty
        if (socialLinks[socialLinks.length - 1].trim() !== '') {
            setSocialLinks([...socialLinks, '']);
            setValidationErrors([...validationErrors, false]);
        } else {
            // Show error for empty field
            const newErrors = [...validationErrors];
            newErrors[newErrors.length - 1] = true;
            setValidationErrors(newErrors);
        }
    };

    const handleRemoveSocialLink = (index) => {
        if (socialLinks.length > 1) {
            const newLinks = socialLinks.filter((_, i) => i !== index);
            const newErrors = validationErrors.filter((_, i) => i !== index);
            setSocialLinks(newLinks);
            setValidationErrors(newErrors);
        }
    };

    const handleSocialLinkChange = (index, value) => {
        const newLinks = [...socialLinks];
        newLinks[index] = value;
        setSocialLinks(newLinks);

        // Clear validation error when user starts typing
        if (value.trim() !== '') {
            const newErrors = [...validationErrors];
            newErrors[index] = false;
            setValidationErrors(newErrors);
        }
    };

    const handleFileSelect = (event) => {
        const file = event.target.files[0];
        if (file) {
            // Validate file type
            if (file.type !== 'application/pdf') {
                setErrors(prev => ({
                    ...prev,
                    resume: 'Please select a PDF file'
                }));
                return;
            }

            // Validate file size (5MB limit)
            if (file.size > 5 * 1024 * 1024) {
                setErrors(prev => ({
                    ...prev,
                    resume: 'File size must be less than 5MB'
                }));
                return;
            }

            setSelectedFile(file);
            setErrors(prev => ({
                ...prev,
                resume: ''
            }));
            console.log('Selected file:', file.name);
        }
    };

    const handleResumeButtonClick = () => {
        fileInputRef.current.click();
    };

    const handleInputChange = (e) => {
        const { name, value } = e.target;
        setFormData(prev => ({
            ...prev,
            [name]: value
        }));

        // Clear error when user starts typing
        if (errors[name]) {
            setErrors(prev => ({
                ...prev,
                [name]: ''
            }));
        }
    };

    const validateForm = () => {
        const newErrors = {};
        let isValid = true;

        // Validate each field
        Object.keys(formData).forEach(field => {
            const error = validateField(field, formData[field]);
            if (error) {
                newErrors[field] = error;
                isValid = false;
            }
        });

        // Validate resume file
        const resumeError = validateField('resume', selectedFile);
        if (resumeError) {
            newErrors.resume = resumeError;
            isValid = false;
        }

        setErrors(newErrors);
        return isValid;
    };

    const convertFileToBase64 = (file) => {
        return new Promise((resolve, reject) => {
            const reader = new FileReader();
            reader.readAsDataURL(file);
            reader.onload = () => resolve(reader.result);
            reader.onerror = error => reject(error);
        });
    };

    const handleSubmit = async () => {
        try {
            setIsSubmitting(true);
            setSubmitError('');

            // Validate all fields
            if (!validateForm()) {
                return;
            }

            // Convert file to base64
            const base64File = await convertFileToBase64(selectedFile);
            console.log('Base64 file:', base64File);

            // Filter out empty social links
            const filteredSocialLinks = socialLinks.filter(link => link && link.trim() !== '');

            // Prepare application data
            const applicationData = {
                firstName: formData.firstName.trim(),
                lastName: formData.lastName.trim(),
                email: formData.email.trim(),
                contactNumber: formData.contactNumber.trim(),
                positionId: position?.id || 'unknown-position',
                positionTitle: position?.title || 'Unknown Position',
                socialLinks: filteredSocialLinks,
                resumeFile: base64File
            };

            // Submit application
            const response = await submitApplication(applicationData);

            if (response.success) {
                setModalMessage('Your application has been submitted successfully. We will review your application and get back to you soon.');
                setSuccessModalOpen(true);
            }
        } catch (error) {
            setModalMessage('Failed to submit application. Please try again later.');
            setFailedModalOpen(true);
            console.error('Error submitting application:', error);
            setSubmitError(error.message || 'Failed to submit application');
        } finally {
            setIsSubmitting(false);
        }
    };

    const handleSuccessModalClose = () => {
        setSuccessModalOpen(false);
        // Reset form
        setFormData({
            firstName: '',
            lastName: '',
            email: '',
            contactNumber: ''
        });
        setSocialLinks(['']);
        setSelectedFile(null);
        setErrors({});
        if (fileInputRef.current) {
            fileInputRef.current.value = '';
        }
    };

    const handleFailedModalClose = () => {
        setFailedModalOpen(false);
    };

    return (
        <>
            <Box
                ref={ref}
                sx={{
                    
                    alignItems: 'center',
                    justifyContent: 'center',
                    p: { xs: 2, md: 5 },
                    mt: 4,
                    mx: { xs: 0, md: 6 },
                    color: '#fff',
                    fontFamily: 'Inter, sans-serif',
                    maxWidth: '90%',
                    width: '100%',
                }}
            >
                <Typography
                    sx={{
                        color: 'rgba(191, 158, 254, 1)',
                        fontWeight: 700,
                        fontSize: { xs: '24px', md: '32px' },
                        mb: 3,
                        textAlign: { xs: 'center', md: 'left' },
                    }}
                >
                    Submit Your Application
                </Typography>
                <Box sx={{ mx: { xs: 2, md: 6 }, width: { xs: '100%', md: '90%' } }}>
                    {/* Hidden file input */}
                    <input
                        type="file"
                        ref={fileInputRef}
                        onChange={handleFileSelect}
                        accept=".pdf"
                        style={{ display: 'none' }}
                    />
                    <Box sx={{ display: { xs: 'flex', md: 'block' }, justifyContent: { xs: 'center', md: 'flex-start' }, width: '100%', mb: 3 }}>
                        <Button
                            variant="outlined"
                            onClick={handleResumeButtonClick}
                            sx={{
                                color: '#fff',
                                borderColor: 'rgba(191, 158, 254, 1)',
                                textTransform: 'none',
                                borderRadius: '8px',
                                fontWeight: 700,
                                fontSize: { xs: '14px', md: '24px' },
                                '&:hover': { borderColor: 'rgba(191, 158, 254, 1)', background: 'rgba(188,166,247,0.08)' },
                            }}
                            fullWidth={false}
                        >
                            {selectedFile ? `Selected: ${selectedFile.name}` : 'Apply with resume*'}
                        </Button>
                    </Box>
                    {errors.resume && (
                        <Typography
                            sx={{
                                color: '#ff6b6b',
                                fontSize: { xs: '12px', md: '14px' },
                                fontFamily: 'Inter, sans-serif',
                                mb: 2,
                                ml: 1
                            }}
                        >
                            {errors.resume}
                        </Typography>
                    )}
                    <Grid container spacing={3} sx={{ mb: 2 }}>
                        <Grid item xs={12} md={6}>
                            <Typography sx={{ color: '#fff', fontFamily: 'Inter, sans-serif', fontWeight: 600, mb: 1, fontSize: { xs: '14px', md: '24px' } }}>First name*</Typography>
                            <TextField
                                variant="outlined"
                                fullWidth
                                name="firstName"
                                value={formData.firstName}
                                onChange={handleInputChange}
                                error={!!errors.firstName}
                                helperText={errors.firstName}
                                InputProps={{
                                    disableUnderline: true,
                                    style: {
                                        background: '#232228',
                                        color: '#fff',
                                        border: 'none',
                                        borderRadius: 6,
                                        padding: '10px 12px',
                                    },
                                }}
                                sx={{
                                    '& .MuiFormHelperText-root': {
                                        color: '#ff6b6b',
                                        marginLeft: 0,
                                        marginTop: 1,
                                        fontSize: { xs: '12px', md: '14px' },
                                        fontFamily: 'Inter, sans-serif',
                                    },
                                }}
                                required
                            />
                        </Grid>
                        <Grid item xs={12} md={6}>
                            <Typography sx={{ color: '#fff', fontFamily: 'Inter, sans-serif', fontWeight: 600, mb: 1, fontSize: { xs: '14px', md: '24px' } }}>Last name*</Typography>
                            <TextField
                                variant="outlined"
                                fullWidth
                                name="lastName"
                                value={formData.lastName}
                                onChange={handleInputChange}
                                error={!!errors.lastName}
                                helperText={errors.lastName}
                                InputProps={{
                                    disableUnderline: true,
                                    style: {
                                        background: '#232228',
                                        color: '#fff',
                                        border: 'none',
                                        borderRadius: 6,
                                        padding: '10px 12px',
                                    },
                                }}
                                sx={{
                                    '& .MuiFormHelperText-root': {
                                        color: '#ff6b6b',
                                        marginLeft: 0,
                                        marginTop: 1,
                                        fontSize: { xs: '12px', md: '14px' },
                                        fontFamily: 'Inter, sans-serif',
                                    },
                                }}
                                required
                            />
                        </Grid>
                        <Grid item xs={12} md={6}>
                            <Typography sx={{ color: '#fff', fontFamily: 'Inter, sans-serif', fontWeight: 600, mb: 1, fontSize: { xs: '14px', md: '24px' } }}>E-mail*</Typography>
                            <TextField
                                variant="outlined"
                                fullWidth
                                name="email"
                                value={formData.email}
                                onChange={handleInputChange}
                                error={!!errors.email}
                                helperText={errors.email}
                                InputProps={{
                                    disableUnderline: true,
                                    style: {
                                        background: '#232228',
                                        color: '#fff',
                                        border: 'none',
                                        borderRadius: 6,
                                        padding: '10px 12px',
                                    },
                                }}
                                sx={{
                                    '& .MuiFormHelperText-root': {
                                        color: '#ff6b6b',
                                        marginLeft: 0,
                                        marginTop: 1,
                                        fontSize: { xs: '12px', md: '14px' },
                                        fontFamily: 'Inter, sans-serif',
                                    },
                                }}
                                required
                            />
                        </Grid>
                        <Grid item xs={12} md={6}>
                            <Typography sx={{ color: '#fff', fontFamily: 'Inter, sans-serif', fontWeight: 600, mb: 1, fontSize: { xs: '14px', md: '24px' } }}>Contact number*</Typography>
                            <TextField
                                variant="outlined"
                                fullWidth
                                name="contactNumber"
                                value={formData.contactNumber}
                                onChange={handleInputChange}
                                error={!!errors.contactNumber}
                                helperText={errors.contactNumber}
                                InputProps={{
                                    disableUnderline: true,
                                    style: {
                                        background: '#232228',
                                        color: '#fff',
                                        border: 'none',
                                        borderRadius: 6,
                                        padding: '10px 12px',
                                    },
                                }}
                                sx={{
                                    '& .MuiFormHelperText-root': {
                                        color: '#ff6b6b',
                                        marginLeft: 0,
                                        marginTop: 1,
                                        fontSize: { xs: '12px', md: '14px' },
                                        fontFamily: 'Inter, sans-serif',
                                    },
                                }}
                                required
                            />
                        </Grid>
                    </Grid>
                    <Typography sx={{ color: '#fff', fontFamily: 'Inter, sans-serif', fontWeight: 600, fontSize: { xs: '14px', md: '24px' }, mb: 1 }}>
                        Social network and web links
                    </Typography>
                    <Typography sx={{ color: 'rgba(147, 147, 152, 1)', fontFamily: 'Inter, sans-serif', fontSize: { xs: '12px', md: '16px' }, mb: 1 }}>
                        Provide us with links to see some of your work (IG / Dribbble/ Behance/ Pinterest/ Blog/ Medium)
                    </Typography>
                    {socialLinks.map((link, index) => (
                        <Box key={index}>
                            <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                                <TextField
                                    variant="outlined"
                                    fullWidth
                                    name={`socialLinks[${index}]`}
                                    value={link}
                                    onChange={(e) => handleSocialLinkChange(index, e.target.value)}
                                    InputProps={{
                                        disableUnderline: true,
                                        style: {
                                            background: '#232228',
                                            color: '#fff',
                                            border: 'none',
                                            borderRadius: 6,
                                            padding: '10px 12px',
                                        },
                                    }}
                                    required
                                />
                                {socialLinks.length > 1 && (
                                    <Button
                                        variant="contained"
                                        onClick={() => handleRemoveSocialLink(index)}
                                        sx={{
                                            minWidth: 40,
                                            minHeight: 40,
                                            background: '#232228',
                                            color: '#fff',
                                            borderRadius: '8px',
                                            fontSize: 24,
                                            p: 0,
                                            boxShadow: 'none',
                                            ml: 1,
                                            '&:hover': { background: '#2d2c32' },
                                        }}
                                    >
                                        -
                                    </Button>
                                )}
                                {index === socialLinks.length - 1 && (
                                    <Button
                                        variant="contained"
                                        onClick={handleAddSocialLink}
                                        sx={{
                                            minWidth: 40,
                                            minHeight: 40,
                                            background: '#232228',
                                            color: '#fff',
                                            borderRadius: '8px',
                                            fontSize: 24,
                                            p: 0,
                                            boxShadow: 'none',
                                            ml: 1,
                                            '&:hover': { background: '#2d2c32' },
                                        }}
                                    >
                                        +
                                    </Button>
                                )}
                            </Box>
                            {validationErrors[index] && (
                                <Typography
                                    sx={{
                                        color: '#ff6b6b',
                                        fontSize: { xs: '12px', md: '14px' },
                                        fontFamily: 'Inter, sans-serif',
                                        mb: 1,
                                        ml: 1
                                    }}
                                >
                                    This field cannot be empty
                                </Typography>
                            )}
                        </Box>
                    ))}
                    {/* <Box sx={{ border: '1px solid rgba(40, 40, 40, 1)', borderRadius: '8px', mb: 3, p: 2 }}>
                        <Typography sx={{ color: '#fff', fontFamily: 'Inter, sans-serif', fontWeight: 600, fontSize: { xs: '14px', md: '24px' }, mb: 1 }}>
                            Experience
                        </Typography>
                        <Button
                            sx={{
                                color: '#fff',
                                background: 'rgba(24, 23, 26, 1)',
                                textTransform: 'none',
                                fontWeight: 600,
                                px: 2,
                                fontFamily: 'Wix Madefor Display, sans-serif',
                                fontSize: { xs: '14px', md: '24px' },
                                '&:hover': { background: 'rgba(40, 40, 40, 1)' },
                            }}
                            fullWidth={false}
                            startIcon={<AddIcon sx={{ fontSize: 50 }} />}
                        >
                            Add Work Experience
                        </Button>
                    </Box>
                    <Box sx={{ border: '1px solid #232228', borderRadius: '8px', mb: 3, p: 2 }}>
                        <Typography sx={{ color: '#fff', fontFamily: 'Inter, sans-serif', fontWeight: 600, fontSize: { xs: '14px', md: '24px' }, mb: 1 }}>
                            Education
                        </Typography>
                        <Button
                            sx={{
                                color: '#fff',
                                background: 'rgba(24, 23, 26, 1)',
                                textTransform: 'none',
                                fontWeight: 600,
                                px: 2,
                                fontFamily: 'Wix Madefor Display, sans-serif',
                                fontSize: { xs: '14px', md: '24px' },
                                '&:hover': { background: 'rgba(40, 40, 40, 1)' },
                            }}
                            fullWidth={false}
                            startIcon={<AddIcon sx={{ fontSize: 50 }} />}
                        >
                            Add Education
                        </Button>
                    </Box> */}

                    {/* Error message */}
                    {submitError && (
                        <Typography
                            sx={{
                                color: '#ff6b6b',
                                fontSize: { xs: '14px', md: '16px' },
                                fontFamily: 'Inter, sans-serif',
                                mb: 2,
                                textAlign: 'center'
                            }}
                        >
                            {submitError}
                        </Typography>
                    )}

                    <Button
                        variant="contained"
                        onClick={handleSubmit}
                        disabled={isSubmitting}
                        sx={{
                            background: 'rgba(191, 158, 254, 1)',
                            color: '#18171B',
                            fontWeight: 700,
                            fontSize: { xs: '14px', md: '24px' },
                            fontFamily: 'Wix Madefor Display, sans-serif',
                            borderRadius: '8px',
                            mt: 2,
                            textTransform: 'none',
                            boxShadow: 'none',
                            '&:hover': { background: '#a88be6' },
                            '&:disabled': {
                                background: '#666',
                                color: '#999'
                            },
                        }}
                    >
                        {isSubmitting ? 'Submitting...' : 'Submit Application'}
                    </Button>
                </Box>
            </Box>

            {/* Success Modal */}
            <SuccessPopupModal
                open={successModalOpen}
                onClose={handleSuccessModalClose}
                title="Application submitted successfully!"
                message={modalMessage}
            />

            {/* Failed Modal */}
            <FailedPopupModal
                open={failedModalOpen}
                onClose={handleFailedModalClose}
                title="Application submission failed!"
                message={modalMessage}
            />
        </>
    );
});

export default ApplicationComponent;

export { JobDescription, ApplicationForm };